import {
  email,
  required,
  TextInput,
  NumberInput,
  DateInput,
  DateTimeInput,
  AutocompleteInput,
  TabbedForm,
  useGetList,
  useNotify,
  useRecordContext,
  useDataProvider,
  SelectInput,
  useTranslate,
} from "react-admin";
import { ReferenceInput, PhoneInputWithCountry } from "@components";
import { CityAutocompleteInput } from "@components/CityAutocompleteInput";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { JobApplication } from "./JobApplicationCard";

const validateRequired = [required()];
const validateEmail = [email()];

// Helper function for user record representation (same as in resources/index.ts)
const getUserRecordRepresentation = (record: { name?: string; email?: string }) => {
  const name = record?.name || "";
  const email = record?.email || "";
  if (!name) {
    return email;
  }
  if (!email) {
    return name;
  }
  return `${name} <${email}>`;
};

export const behavioralProfileChoices = [
  { id: "dominant" },
  { id: "influential" },
  { id: "steady" },
  { id: "compliant" },
].map((choice) => ({
  ...choice,
  name: `resources.job-application.behavioralProfile.${choice.id}`,
}));

// Tab 1: Dados do Candidato
export const CandidateDataTab = () => {
  const notify = useNotify();
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const currentId = record?.id;

  // Create validators that check for duplicates
  const validateEmailUnique = useMemo(() => {
    return async (value: string | undefined) => {
      if (!value) return undefined;
      
      // First validate email format
      const emailError = email()(value);
      if (emailError) return emailError;
      
      try {
        // Check for duplicates using dataProvider
        const { data } = await dataProvider.getList('job-application', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'DESC' },
          filter: { email_eq: value },
        });
        
        // If found a duplicate and it's not the current record being edited
        if (data.length > 0) {
          const existing = data[0];
          // Check if it's a new record (id is 'create' or undefined) or if it's a different existing record
          const isNewRecord = !currentId || currentId === 'create';
          const isDifferentRecord = currentId && existing.id !== currentId;
          
          if (isNewRecord || isDifferentRecord) {
            const message = `Já existe uma candidatura com o e-mail "${value}".`;
            notify(message, { type: 'warning' });
            return message;
          }
        }
      } catch (error) {
        // If check fails, don't block the form (fail gracefully)
        console.warn('Failed to check email uniqueness:', error);
      }
      
      return undefined;
    };
  }, [notify, dataProvider, currentId]);

  const validatePhoneUnique = useMemo(() => {
    return async (value: string | undefined) => {
      if (!value) return undefined;
      
      try {
        // Check for duplicates using dataProvider
        const { data } = await dataProvider.getList('job-application', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'DESC' },
          filter: { phone_eq: value },
        });
        
        // If found a duplicate and it's not the current record being edited
        if (data.length > 0) {
          const existing = data[0];
          // Check if it's a new record (id is 'create' or undefined) or if it's a different existing record
          const isNewRecord = !currentId || currentId === 'create';
          const isDifferentRecord = currentId && existing.id !== currentId;
          
          if (isNewRecord || isDifferentRecord) {
            const message = `Já existe uma candidatura com o telefone "${value}".`;
            notify(message, { type: 'warning' });
            return message;
          }
        }
      } catch (error) {
        // If check fails, don't block the form (fail gracefully)
        console.warn('Failed to check phone uniqueness:', error);
      }
      
      return undefined;
    };
  }, [notify, dataProvider, currentId]);

  return (
    <>
      <TextInput source="candidateName" validate={validateRequired} fullWidth />
      <TextInput 
        source="email" 
        validate={[validateEmail, validateEmailUnique]} 
        fullWidth 
      />
      <PhoneInputWithCountry 
        source="phone" 
        defaultCountry="BR"
        validate={validatePhoneUnique}
        fullWidth
      />
      <DateInput source="birthDate" fullWidth />
      <CityAutocompleteInput source="city" fullWidth />
    </>
  );
};

type JobApplicationStatusData = {
  id: number;
  name: string;
  order: number;
  isTerminal?: boolean;
  requiresClosureReason?: boolean;
};

/**
 * Status field + inline closure reason. When the selected status is
 * isTerminal && requiresClosureReason (e.g. "Encerrado"), an inline
 * "closure reason" picker is rendered right below the status, filtered by the
 * application's phase (pre_hire vs post_hire). No modal in the Edit form — the
 * CloseApplicationDialog is only used in the kanban drag flow (List.tsx).
 *
 * Edge cases handled:
 * - statusId changes away from a terminal status -> closureReasonId is cleared
 *   so a stale reason can't be submitted alongside a non-terminal status.
 * - submit is blocked (validateClosureReasonRequired, on the reason field) if
 *   a terminal status is selected without a closure reason.
 */
const StatusFieldWithClosure = () => {
  const { setValue, getValues } = useFormContext();
  const record = useRecordContext<JobApplication>();
  const translate = useTranslate();

  // Fetch all statuses so we can read the flags client-side without N requests.
  const { data: statuses = [] } = useGetList<JobApplicationStatusData>(
    "job-application-status",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "order", order: "ASC" },
    },
  );

  const statusId = useWatch({ name: "statusId" }) as number | undefined;
  const outcome = useWatch({ name: "outcome" }) as string | undefined;

  const selectedStatus = useMemo(
    () => statuses.find((s) => s.id === statusId),
    [statuses, statusId],
  );

  const needsClosureReason = Boolean(
    selectedStatus?.isTerminal && selectedStatus?.requiresClosureReason,
  );

  // Phase the closure reasons must match. outcome can be edited in the same
  // form, so prefer the live form value over the record.
  const effectiveOutcome = outcome ?? record?.outcome;
  const expectedPhase: "pre_hire" | "post_hire" =
    effectiveOutcome === "hired" ? "post_hire" : "pre_hire";

  // Closure reasons via the search endpoint (it returns phase/isActive; the
  // /references endpoint only returns id+name and can't filter by phase).
  // Same approach as CloseApplicationDialog. Phase filtering is client-side.
  const { data: closureReasons = [] } = useGetList<{
    id: number;
    name: string;
    phase: "pre_hire" | "post_hire";
    isActive: boolean;
  }>(
    "job-application-closure-reason",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "order", order: "ASC" },
      filter: { isActive_eq: true },
    },
    { enabled: needsClosureReason },
  );

  const closureReasonChoices = useMemo(
    () => closureReasons.filter((r) => r.phase === expectedPhase),
    [closureReasons, expectedPhase],
  );

  // Clear a stale closureReasonId when the status is no longer terminal.
  // (User picks Encerrado, fills the reason, then switches to another status.)
  useEffect(() => {
    if (statusId === undefined || statuses.length === 0) return;
    if (!needsClosureReason) {
      const current = getValues("closureReasonId");
      if (current !== undefined && current !== null && current !== "") {
        setValue("closureReasonId", null, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [statusId, needsClosureReason, statuses.length, getValues, setValue]);

  // Status only validates "required" — the missing-closure-reason error is
  // surfaced on the closure reason field itself (below), not duplicated here.
  const validateStatusRequired = useMemo(() => {
    return (value: number | undefined) =>
      value === undefined || value === null
        ? translate("ra.validation.required", { _: "Required" })
        : undefined;
  }, [translate]);

  // Submit blocker: the closure reason field carries the real invariant.
  // When the status needs a reason but none is set, this fails validation.
  const validateClosureReasonRequired = useMemo(() => {
    return (value: number | undefined) => {
      if (
        needsClosureReason &&
        (value === undefined || value === null || (value as unknown) === "")
      ) {
        return translate(
          "resources.job-application.closeApplication.reasonRequired",
          { _: "Select a closure reason before saving" },
        );
      }
      return undefined;
    };
  }, [needsClosureReason, translate]);

  return (
    <>
      <ReferenceInput
        source="statusId"
        reference="job-application-status"
        fullWidth
      >
        <AutocompleteInput
          optionText="name"
          validate={validateStatusRequired}
          filterToQuery={(searchText: string) => ({ name_q: searchText })}
        />
      </ReferenceInput>
      {needsClosureReason ? (
        <SelectInput
          source="closureReasonId"
          label="resources.job-application.closeApplication.reasonLabel"
          choices={closureReasonChoices}
          optionText="name"
          optionValue="id"
          validate={validateClosureReasonRequired}
          fullWidth
        />
      ) : (
        // Keep the field registered (as a number) even when hidden so the
        // value is always part of the form payload.
        <NumberInput
          source="closureReasonId"
          sx={{ display: "none" }}
          helperText={false}
        />
      )}
    </>
  );
};

// Tab 2: Perfil e Referências
export const ProfileAndReferencesTab = () => (
  <>
    <StatusFieldWithClosure />
    <AutocompleteInput source="behavioralProfile" choices={behavioralProfileChoices} fullWidth />
    <ReferenceInput source="sourceId" reference="job-application-source" fullWidth>
      <AutocompleteInput
        optionText="name"
        filterToQuery={(searchText: string) => ({ name_q: searchText })}
      />
    </ReferenceInput>
    <ReferenceInput source="companyId" reference="company" fullWidth>
      <AutocompleteInput
        optionText="name"
        filterToQuery={(searchText: string) => ({ name_q: searchText })}
      />
    </ReferenceInput>
  </>
);

// Outcome is DERIVED from the pipeline status (hire status, or terminal status
// + closure reason) — shown read-only here, never set manually. Only the 3 core
// values exist (hired/closed/terminated); the legacy values were normalized away.
const outcomeChoices = [
  { id: 'hired', name: 'resources.job-application.fields.outcomeChoices.hired' },
  { id: 'closed', name: 'resources.job-application.fields.outcomeChoices.closed' },
  { id: 'terminated', name: 'resources.job-application.fields.outcomeChoices.terminated' },
];

// Tab 3: Processo e Observações
export const ProcessAndObservationsTab = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  
  return (
    <>
      <ReferenceInput source="interviewResponsibleId" reference="user" fullWidth>
        <AutocompleteInput 
          optionText={getUserRecordRepresentation}
          filterToQuery={(searchText: string) => ({ nameEmail_q: searchText })}
        />
      </ReferenceInput>
      <ReferenceInput source="interviewSchedulingResponsibleId" reference="user" fullWidth>
        <AutocompleteInput 
          optionText={getUserRecordRepresentation}
          filterToQuery={(searchText: string) => ({ nameEmail_q: searchText })}
        />
      </ReferenceInput>
      <ReferenceInput source="supervisorId" reference="user" fullWidth>
        <AutocompleteInput 
          optionText={getUserRecordRepresentation}
          filterToQuery={(searchText: string) => ({ nameEmail_q: searchText })}
        />
      </ReferenceInput>
      <DateTimeInput
        source="interviewDate"
        fullWidth
        parse={(value: string) => {
          // If value is empty, return null
          if (!value) return null;

          // DateTimeInput returns format: "YYYY-MM-DDTHH:mm"
          // We need to convert this local time to UTC ISO string
          // The user's input is in their local timezone (GMT-3 in this case)
          // So "2026-01-06T14:00" should become "2026-01-06T17:00:00.000Z" (14:00 GMT-3 = 17:00 UTC)
          const localDate = new Date(value);
          return localDate.toISOString();
        }}
        format={(value: string) => {
          // When displaying, convert UTC ISO string back to local datetime-local format
          if (!value) return '';

          // Server returns: "2026-01-06T17:00:00.000Z"
          // We need to show user: "2026-01-06T14:00" (their local time GMT-3)
          const date = new Date(value);

          // Format as YYYY-MM-DDTHH:mm (datetime-local format)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');

          return `${year}-${month}-${day}T${hours}:${minutes}`;
        }}
      />
      <DateInput
        source="startedAt"
        fullWidth
        parse={(value: string) => value || null}
        format={(value: string) => value || ''}
      />
      <SelectInput
        source="outcome"
        choices={outcomeChoices}
        label="resources.job-application.fields.outcome"
        emptyText="Nenhum (em pipeline)"
        parse={(value: string) => value === '' ? null : value}
        // Read-only when editing an existing record: the outcome is derived
        // from the pipeline status and the BE rejects any manual non-null change
        // (400). Stays interactive in the Create modal (record has no id there),
        // which the create flow uses to drive the closure-reason phase.
        readOnly={!!record?.id}
        helperText={translate('resources.job-application.fields.outcomeHelperText', {
          _: 'Derivado automaticamente do status. Para contratar ou encerrar, mude o status do candidato (com motivo, quando exigido).'
        })}
        fullWidth
      />
      <TextInput source="observations" rows={8} fullWidth resettable={true} />
    </>
  );
};

// JobApplicationForm - wraps fields in TabbedForm
// According to React Admin docs:
// - CreateDialog/EditDialog can contain TabbedForm directly
// - TabbedForm provides its own form context and toolbar
export const JobApplicationForm = (props: Record<string, unknown>) => {
  const { data: statuses = [] } = useGetList('job-application-status', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
  });

  // Set default statusId to the first status for new records
  const defaultValues = {
    statusId: statuses.length > 0 ? statuses[0].id : undefined,
  };

  // Extract toolbar from props if provided, otherwise TabbedForm will use default
  const { toolbar, ...restProps } = props;

  // For CreateDialog/EditDialog or standalone use, use TabbedForm
  return (
    <TabbedForm 
      warnWhenUnsavedChanges 
      {...restProps} 
      defaultValues={defaultValues}
      toolbar={toolbar}
    >
      <TabbedForm.Tab label="resources.job-application.tabs.candidateData">
        <CandidateDataTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab label="resources.job-application.tabs.profileAndReferences">
        <ProfileAndReferencesTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab label="resources.job-application.tabs.processAndObservations">
        <ProcessAndObservationsTab />
      </TabbedForm.Tab>
    </TabbedForm>
  );
};

export { JobApplicationList } from './List';
export { OverviewDashboard as JobApplicationDashboard } from './dashboards/Overview';
export { JobApplicationEdit } from './Edit';
export { JobApplicationCalendar } from './CalendarView';
